import { env } from "@/env";

/**
 * Normalizes a stored attachment URL for playback: S3 object URLs are mapped to the
 * serving CDN, and a legacy `.webm` object is played as its `.mp3` sibling because
 * Safari / iOS do not decode WebM/Opus reliably.
 *
 * THE `original/` SEGMENT IS NEVER REWRITTEN. Media-v3 keys an attachment's folder as
 * `<attachmentId>/original/<slug>` for the upload as received and `<attachmentId>/<base>.<ext>`
 * for the served rewrite (`media/src/v3/lib/keys.ts`) — the segment is exactly what keeps
 * the two apart, so the mp3 rendition of `<id>/original/clip.webm` is `<id>/clip.mp3`, NOT
 * `<id>/original/clip.mp3`. Swapping the extension in place therefore mints a key that has
 * never existed in any bucket, and the CDN answers 403: that is the bug this guard fixes
 * (production, an office chat voice memo —
 * `Failed to fetch .../original/recording-1788356359806.mp3: 403`; the browser recorder can
 * only ever produce webm/m4a/ogg, so the `.mp3` was manufactured here).
 *
 * AND THE ORIGINAL ITSELF IS NOT PLAYABLE EITHER: the CDN serves renditions only — the
 * `original/` object answers 403 even when it exists (production, 2026-09-02:
 * `…/original/recording-1788356359806.webm: 403` while `…/recording-1788356359806.mp3` was
 * 200). So an original is mapped to its SERVED AUDIO RENDITION, `<id>/<base>.mp3` — the
 * same formula media-v3 uses (`rewriteKey(mediaBase(key), "mp3")`). Until the rendition has
 * landed that url is a miss the player reports quietly; `use-attachment-processing` then
 * swaps in the authoritative `contentUri` (the same key) and the load succeeds.
 *
 * @param url the attachment's stored URL; `null`/`undefined`/blank are normal inputs.
 * @returns a playable absolute URL, or `undefined` when there is nothing to play.
 */
export function convertAudioUrlToPlayable(
  url: string | null | undefined,
): string | undefined {
  if (!url?.trim()) return undefined;
  const onServingCdn = url.includes(env.PUBLIC_S3_MEDIA_URL ?? "")
    ? url.replace(
        `${env.PUBLIC_S3_MEDIA_URL ?? ""}`,
        `${env.PUBLIC_SERVING_MEDIA_URL ?? ""}`,
      )
    : url;
  return (
    originalToRendition(onServingCdn) ?? onServingCdn.replace(".webm", ".mp3")
  );
}

/**
 * Pure. For a media-v3 upload-as-received URL — one whose key is exactly
 * `<attachmentId>/original/<slug>` (the upload key media/src/lib/storage/keys.ts
 * `buildUploadKey` builds) — the URL of its served audio rendition, `<attachmentId>/<base>.mp3` on the
 * same host. Undefined for anything else (a served key, a malformed URL), so the caller
 * keeps its legacy behaviour.
 */
function originalToRendition(url: string): string | undefined {
  try {
    const parsed = new URL(url);
    const segments = parsed.pathname.replace(/^\/+/, "").split("/");
    if (segments.length !== 3 || segments[1] !== "original") return undefined;
    const name = segments[2];
    const dot = name.lastIndexOf(".");
    const base = dot > 0 ? name.slice(0, dot) : name;
    if (!base) return undefined;
    parsed.pathname = `/${segments[0]}/${base}.mp3`;
    return parsed.toString();
  } catch {
    return undefined;
  }
}
